import { BaseSection } from 'components/layout/Section/BaseSection';
import { NsKey } from 'enums/NsKey';
import { InputFactory } from 'factories/InputFactory';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericMessage } from 'components/feedback/Message';
import { useFormikContext } from 'formik';
import { Divider } from 'components/ui/Divider';
import {
  SpecialConditionsFormFieldNames,
  SpecialConditionsFormValues,
  calculateDueDate
} from './specialConditionForm';
import { specialConditionsInputsConfig } from './specialConditionForm.inputs';
import { InputText } from 'components/forms/InputText';
import { BankAccountRibSelector } from '../../components/bank-account';
import { useAppSelector } from 'app/hooks';
import { ReferentialKey } from 'api/referential';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';
import { GenericQuestion } from 'features/subscription-process/profiling-process/components/generic/GenericQuestion';
import { IconPossibleAnswer, PossibleAnswer, Question } from 'features/subscription-process/profiling-process/types/interfaces';
import { BorrowerQuoteTabDto } from 'api/quote';
import { useCoBorrowerBankAccountPrefill } from './useCoBorrowerBankAccountPrefill';

export type InsuranceProjectType = 'RENEG_BANQUE' | 'RENEG_COURTIER' | null;
/** @deprecated Use InsuranceProjectType instead */
export type RenegotiationType = InsuranceProjectType;

interface SpecialConditionsLayoutProps {
  insuranceProjectCode: string | null;
  selectedInsuranceProjectCode: string | null;
  onInsuranceProjectCodeChange: (code: string | null) => void;
  personId?: string;
  quoteId: string;
  quoteTabs?: BorrowerQuoteTabDto[] | null;
  risqueAssureId?: string;
}

export const SpecialConditionsLayout: React.FC<SpecialConditionsLayoutProps> = ({
  insuranceProjectCode,
  selectedInsuranceProjectCode,
  onInsuranceProjectCodeChange,
  personId,
  quoteId,
  quoteTabs,
  risqueAssureId
}) => {
  const { t } = useTranslation([NsKey.Contractualization]);
  const { values, setFieldValue } = useFormikContext<SpecialConditionsFormValues>();
  const insuranceProjectReferential = useAppSelector(selectReferentialDataByKey(ReferentialKey.InsuranceProject));

  const selectedCode = selectedInsuranceProjectCode;

  const bankInfoRef = useRef<HTMLDivElement>(null);
  const userHasInteracted = useRef(false);

  const insuranceProjectType: InsuranceProjectType = useMemo(() => {
    if (selectedCode === 'RENEG_BANQUE') return 'RENEG_BANQUE';
    if (selectedCode === 'RENEG_COURTIER') return 'RENEG_COURTIER';
    return null;
  }, [selectedCode]);

  useEffect(() => {
    if (!userHasInteracted.current) {
      return;
    }
    if (insuranceProjectType !== null) {
      setTimeout(() => {
        bankInfoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
    userHasInteracted.current = false;
  }, [insuranceProjectType]);

  const insuranceProjectQuestion = useMemo((): Question | null => {
    if (!insuranceProjectReferential || insuranceProjectReferential.length === 0) return null;
    return {
      key: 'insuranceProject',
      label: '',
      isMultipleChoice: false,
      possibleAnswers: insuranceProjectReferential.filter(ref => ref.code !== 'NE_SAIS_PAS').map(ref => ({
        id: ref.id,
        key: ref.code,
        label: ref.label,
        iconKey: ref.iconKey || '',
      } as IconPossibleAnswer))
    };
  }, [insuranceProjectReferential]);

  const selectedInsuranceProject = useMemo((): IconPossibleAnswer[] => {
    if (!selectedCode || !insuranceProjectReferential) return [];
    const match = insuranceProjectReferential.find(ref => ref.code === selectedCode);
    if (!match || !match.iconKey) return [];
    return [{
      id: match.id,
      key: match.code,
      label: match.label,
      iconKey: match.iconKey,
    }];
  }, [selectedCode, insuranceProjectReferential]);

  const handleInsuranceProjectAnswer = useCallback((_key: string, answer: PossibleAnswer[]) => {
    const selected = answer[0] as IconPossibleAnswer;
    if (selected?.key) {
      userHasInteracted.current = true;
      onInsuranceProjectCodeChange(selected.key as string);
    }
  }, [onInsuranceProjectCodeChange]);

  const handleBankAccountSelect = useCallback((bankAccountId: string) => {
    setFieldValue(SpecialConditionsFormFieldNames.BankAccountId, bankAccountId);
  }, [setFieldValue]);

  const { prefilledFromName, refreshKey } = useCoBorrowerBankAccountPrefill({
    quoteId,
    personId,
    quoteTabs,
    selectedBankAccountId: values.bankAccountId,
    onPrefilled: handleBankAccountSelect,
    risqueAssureId
  });

  const renderField = useCallback(
    (fieldName: SpecialConditionsFormFieldNames) => {
      return (
        <InputFactory
          config={specialConditionsInputsConfig}
          fieldName={fieldName}
          state={values}
        />
      );
    },
    [values]
  );

  const dueDate = useMemo(() => {
    const date = calculateDueDate(values.startDate);
    if (!date) return null;
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }, [values.startDate]);

  return (
    <div className="flex flex-col gap-4">
      {/* Section 1: Date d'effet, échéance et fractionnement */}
      <BaseSection
        title={t('CONTRACTUALIZATION.SPECIAL_CONDITIONS.SECTIONS.EFFECTIVE_DATE')}
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            {renderField(SpecialConditionsFormFieldNames.StartDate)}
          </div>
          <div>
            <InputText
              label={t('CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.DUE_DATE')}
              value={dueDate || ''}
              disabled
            />
          </div>
          <div className="col-span-2">
            {renderField(SpecialConditionsFormFieldNames.FrequencyCode)}
          </div>
        </div>
        <GenericMessage
          className="mt-4"
          type="warning"
          dismissible={false}
          message={t(
            'CONTRACTUALIZATION.SPECIAL_CONDITIONS.MESSAGES.START_DATE_INFO'
          )}
        />
      </BaseSection>

      {/* Section 3: Compte bancaire */}
      <BaseSection
        title={t('CONTRACTUALIZATION.SPECIAL_CONDITIONS.SECTIONS.BANK_ACCOUNT')}
      >
        {personId &&
          <div className='pt-2'>
          {prefilledFromName && (
            <GenericMessage
              type="info"
              dismissible={false}
              message={t('CONTRACTUALIZATION.BANK_ACCOUNT.PREFILLED_FROM_FIRST_BORROWER', { name: prefilledFromName })}
              className="mb-3"
            />
          )}

          <BankAccountRibSelector
            personId={personId}
            selectedBankAccountId={values.bankAccountId}
            onSelect={handleBankAccountSelect}
            refreshKey={refreshKey}
          />
        </div>
        }
      </BaseSection>

      <Divider />

      {/* Section: Encours existants */}
      <BaseSection
        title={t('CONTRACTUALIZATION.SPECIAL_CONDITIONS.SECTIONS.EXISTING_OUTSTANDING')}
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            {renderField(SpecialConditionsFormFieldNames.ExistingOutstanding)}
          </div>
          <div>
            {renderField(SpecialConditionsFormFieldNames.ExistingOutstandingInsurer)}
          </div>
        </div>
      </BaseSection>

      {/* Section 5: Projet d'assurance emprunteur */}
      {insuranceProjectQuestion && (
        <>
          <Divider />

          <BaseSection title="Projet d'assurance emprunteur">
            <GenericQuestion
              question={insuranceProjectQuestion}
              currentAnswers={selectedInsuranceProject}
              onAnswer={handleInsuranceProjectAnswer}
            />
          </BaseSection>
        </>
      )}

      {/* Section 6: Informations banque — affiché pour tous les projets */}
      <div className='space-y-4' ref={bankInfoRef}>
        <Divider />

        <BaseSection title={t('CONTRACTUALIZATION.SPECIAL_CONDITIONS.SECTIONS.BANK_INFO')}>
          <div className="grid grid-cols-6 gap-4 mb-4">
            <div className="col-span-6">
              {renderField(SpecialConditionsFormFieldNames.CurrentLoanNumber)}
            </div>
            <div className="col-span-6">
              {renderField(SpecialConditionsFormFieldNames.BankEntityName)}
            </div>
            <div className="col-span-6">
              {renderField(SpecialConditionsFormFieldNames.BankEntityAddressSearch)}
            </div>
            <div className="col-span-6">
              {renderField(SpecialConditionsFormFieldNames.BankEntityAdditionalAddress)}
            </div>
          </div>

          {/* Contact commercial de la banque */}
          <Divider className="my-4" />

          <p className="text-sm font-medium text-gray-700 mb-3">
            {t('CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.BANK_COMMERCIAL_CONTACT')}
          </p>
          <div className="grid grid-cols-6 gap-4">
            <div className="col-span-3">
              {renderField(SpecialConditionsFormFieldNames.BankLastName)}
            </div>
            <div className="col-span-3">
              {renderField(SpecialConditionsFormFieldNames.BankFirstName)}
            </div>
            <div className="col-span-3">
              {renderField(SpecialConditionsFormFieldNames.BankPhone)}
            </div>
            <div className="col-span-3">
              {renderField(SpecialConditionsFormFieldNames.BankEmail)}
            </div>
          </div>
        </BaseSection>

        {/* Section 7: Contact assurance — uniquement pour RENEG_COURTIER */}
        {insuranceProjectType === 'RENEG_COURTIER' && (
          <>
            <Divider />

            <BaseSection title={t('CONTRACTUALIZATION.SPECIAL_CONDITIONS.SECTIONS.INSURANCE_CONTACT')}>
              <div className="grid grid-cols-6 gap-4 mb-4">
                <div className="col-span-6">
                  {renderField(SpecialConditionsFormFieldNames.InsuranceEntityName)}
                </div>
                <div className="col-span-6">
                  {renderField(SpecialConditionsFormFieldNames.InsuranceEntityAddressSearch)}
                </div>
                <div className="col-span-6">
                  {renderField(SpecialConditionsFormFieldNames.InsuranceEntityAdditionalAddress)}
                </div>
              </div>

              <Divider className="my-4" />

              <div className="grid grid-cols-6 gap-4">
                <div className="col-span-3">
                  {renderField(SpecialConditionsFormFieldNames.InsuranceLastName)}
                </div>
                <div className="col-span-3">
                  {renderField(SpecialConditionsFormFieldNames.InsuranceFirstName)}
                </div>
                <div className="col-span-3">
                  {renderField(SpecialConditionsFormFieldNames.InsurancePhone)}
                </div>
                <div className="col-span-3">
                  {renderField(SpecialConditionsFormFieldNames.InsuranceEmail)}
                </div>
              </div>
            </BaseSection>
          </>
        )}

        <GenericMessage
          className="mt-4"
          type="info"
          dismissible={false}
          message={t(
            'CONTRACTUALIZATION.SPECIAL_CONDITIONS.MESSAGES.RENEGOTIATION_INFO'
          )}
        />
      </div>
    </div>
  );
};
